import { useEffect, useMemo, useState } from "react";
import { fetchRoles } from "../../api/accessControl";
import RoleDetailsDrawer from "./RoleDetailsDrawer";
import AddRoleModal from "./AddRoleModal";

const PAGE_SIZE_OPTIONS = [6, 9, 12, 18];

function RoleCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 shadow-sm animate-pulse dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="h-5 w-32 rounded-md bg-slate-200 dark:bg-slate-800" />
            <div className="h-3 w-20 rounded-md bg-slate-100 dark:bg-slate-800/60" />
          </div>
          <div className="h-8 w-12 rounded-full bg-slate-100 dark:bg-slate-800/60" />
        </div>

        <div className="space-y-2">
          <div className="h-3 w-full rounded-md bg-slate-100 dark:bg-slate-800/60" />
          <div className="h-3 w-11/12 rounded-md bg-slate-100 dark:bg-slate-800/60" />
          <div className="h-3 w-8/12 rounded-md bg-slate-100 dark:bg-slate-800/60" />
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="h-3 w-24 rounded-md bg-slate-100 dark:bg-slate-800/60" />
          <div className="h-8 w-24 rounded-xl bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon, accent = false }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-4 ${
        accent
          ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-black"
          : "border-slate-200 bg-white text-slate-900 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${
              accent
                ? "text-white/70 dark:text-black/70"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </p>
          <p className="mt-2 text-2xl font-semibold tracking-tight">{value}</p>
        </div>

        <div
          className={`flex h-11 w-11 items-center justify-center rounded-2xl text-lg ${
            accent
              ? "bg-white/10 dark:bg-black/10"
              : "bg-slate-100 dark:bg-slate-800"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function RoleCard({ role, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(role)}
      className="group w-full overflow-hidden rounded-3xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700 dark:hover:bg-slate-800/60"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-sm font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                {String(role.name || "R").charAt(0).toUpperCase()}
              </div>

              <div className="min-w-0">
                <h3 className="truncate text-base font-semibold tracking-tight text-slate-900 dark:text-white">
                  {role.name}
                </h3>
                <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  Access Role
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            Role
          </div>
        </div>

        <p className="line-clamp-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {role.description || "No description provided for this role."}
        </p>

        <div className="flex items-center justify-between border-t border-slate-200 pt-3 dark:border-slate-800">
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            Click to view permissions
          </span>

          <span className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 transition group-hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:group-hover:bg-slate-800">
            View Details
          </span>
        </div>
      </div>
    </button>
  );
}

function buildPageNumbers(currentPage, totalPages) {
  const pages = [];
  const add = (value) => {
    if (!pages.includes(value)) pages.push(value);
  };

  add(1);
  for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
    if (i > 1 && i < totalPages) add(i);
  }
  if (totalPages > 1) add(totalPages);

  const finalPages = [];
  for (let i = 0; i < pages.length; i += 1) {
    finalPages.push(pages[i]);
    if (pages[i + 1] && pages[i + 1] - pages[i] > 1) {
      finalPages.push("...");
    }
  }

  return finalPages;
}

export default function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await fetchRoles();
      setRoles(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;

    return roles.filter(
      (role) =>
        role.name?.toLowerCase().includes(q) ||
        role.description?.toLowerCase().includes(q)
    );
  }, [search, roles]);

  const describedRoles = useMemo(
    () => roles.filter((role) => !!role.description?.trim()).length,
    [roles]
  );

  useEffect(() => {
    setPage(1);
  }, [search, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredRoles.length / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedRoles = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredRoles.slice(start, start + pageSize);
  }, [filteredRoles, safePage, pageSize]);

  const visiblePageNumbers = useMemo(
    () => buildPageNumbers(safePage, totalPages),
    [safePage, totalPages]
  );

  const headerStats = useMemo(
    () => ({
      total: roles.length,
      visible: filteredRoles.length,
      documented: describedRoles,
    }),
    [roles.length, filteredRoles.length, describedRoles]
  );

  return (
    <div className="mt-6 space-y-6 text-slate-900 dark:text-slate-100">
      {/* Workspace header */}
      <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_12px_40px_rgba(15,23,42,0.07)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_12px_40px_rgba(0,0,0,0.30)]">
        <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 px-6 py-6 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <div className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-800/70 dark:text-slate-400">
                Access Administration
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Roles & Permissions
              </h1>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                Manage platform roles, review access definitions, and control
                how permissions are grouped across the system.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={loadRoles}
                className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Refresh
              </button>

              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
              >
                <span className="text-base leading-none">＋</span>
                Add Role
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative w-full max-w-xl">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500">
              🔎
            </span>
            <input
              type="text"
              placeholder="Search roles by name or description..."
              className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-4 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-white dark:focus:ring-slate-800"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Total: {roles.length}
            </div>
            <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Results: {filteredRoles.length}
            </div>

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 outline-none transition focus:border-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:focus:border-white"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_10px_35px_rgba(15,23,42,0.06)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_10px_35px_rgba(0,0,0,0.28)]">
        {loading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: pageSize }).map((_, index) => (
              <RoleCardSkeleton key={index} />
            ))}
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="flex min-h-[260px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 px-4 text-center dark:border-slate-800 dark:bg-slate-950/20">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl dark:bg-slate-800">
              🛡️
            </div>
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
              No roles found
            </h3>
            <p className="mt-2 max-w-md text-sm leading-6 text-slate-500 dark:text-slate-400">
              {search.trim()
                ? "No roles match your current search. Try a different keyword."
                : "There are no roles available yet. Create one to begin defining access control."}
            </p>

            {!search.trim() && (
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90 dark:bg-white dark:text-black"
              >
                <span className="text-base leading-none">＋</span>
                Create First Role
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Role Register
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Showing {paginatedRoles.length} of {filteredRoles.length} matching role
                  {filteredRoles.length === 1 ? "" : "s"}.
                </p>
              </div>

              <div className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                Page {safePage} of {totalPages}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {paginatedRoles.map((role) => (
                <RoleCard key={role.id} role={role} onClick={setSelectedRole} />
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-4 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between dark:border-slate-800">
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Showing{" "}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {filteredRoles.length === 0 ? 0 : (safePage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {Math.min(safePage * pageSize, filteredRoles.length)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-900 dark:text-white">
                  {filteredRoles.length}
                </span>{" "}
                roles
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Previous
                </button>

                {visiblePageNumbers.map((item, index) =>
                  item === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-2 text-sm text-slate-400 dark:text-slate-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item)}
                      className={`min-w-[40px] rounded-xl px-3 py-2 text-sm font-semibold transition ${
                        safePage === item
                          ? "bg-black text-white dark:bg-white dark:text-black"
                          : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      }`}
                    >
                      {item}
                    </button>
                  )
                )}

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <RoleDetailsDrawer
        role={selectedRole}
        onClose={() => setSelectedRole(null)}
      />

      <AddRoleModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={loadRoles}
      />
    </div>
  );
}